/**
 * `/customers` — the customer organisations. Port of the demo's
 * `renderCustomers()` (line 2744).
 *
 * One card per organisation Meridian serves: the six mine owners and the three
 * underground contractors working their sites. Each card carries the legal
 * details, the fleet and what is wrong with it, what is waiting on the customer,
 * their satisfaction scores, their contacts, their channel, and the last five
 * notifications the Customer relations agent has drafted for them. The log of
 * every notification this fortnight sits under the grid.
 *
 * ## Data
 *
 * Four domains: `people` (directory, the columnar `css` table), `service`
 * (cases, technician-app jobs), `parts` (orders, bulletins) and `work-orders`
 * (draft counts). Alerts and incidents come from the narrative seeds.
 *
 * ## Declared omissions
 *
 * - **Organisation logos.** The demo prints each customer's SVG mark from
 *   `BRANDJ.svg`; the extractor did not split those per organisation, so the
 *   card carries the organisation's registered colour as a swatch instead. The
 *   colour is data out of `brand.json`, which is why it is an inline style and
 *   not a token.
 * - **"Draft the weekly email"** on each card asks the assistant, as the demo's
 *   button does. Report *generation* (DOCX/PPTX) is a Tier 3 roadmap row.
 *
 * @see src/versions/v1/modules/customers/CustomersScene.transform.ts
 * @see docs/logic/customers/customers.md
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  InlineError,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { unpackTable } from '../../shared/columnar';
import { groupNum } from '../../shared/oem-engine';
import { askOem } from '../../shared/assistant/ask';
import {
  useOemData,
  type CustomerSatisfactionResponse,
  type WorkOrder,
} from '../../shared/useOemData';
import { SceneSection, SourceLink, TonePill } from '../../shared/oem-tiles';
import { useRowsDrawer } from '../service/source-slice';
import {
  channelFor,
  customerCards,
  customerSummary,
  notifications,
  NOTIFICATION_LOG_LIMIT,
  type CustomerCard,
  type CustomerSceneCtx,
  type Notification,
} from './CustomersScene.transform';

/** Demo line 2757. The page's standfirst, verbatim. */
const STANDFIRST =
  'Every customer organisation — owners and their underground contractors — with contacts, ' +
  'fleet, what is waiting on them, their satisfaction scores per job and the notifications the ' +
  'Customer relations agent has drafted for them. Nothing is sent without a person.';

export function CustomersScene() {
  const people = useOemData('people');
  const service = useOemData('service');
  const parts = useOemData('parts');
  const workOrders = useOemData('work-orders');

  const ctx = useMemo<CustomerSceneCtx | null>(() => {
    if (!people.data || !service.data || !parts.data || !workOrders.data) return null;
    return {
      people: people.data.people,
      css: unpackTable<CustomerSatisfactionResponse>(people.data.css),
      techs: people.data.tapp_techs,
      cases: service.data.cases,
      techJobs: service.data.tapp_jobs,
      orders: parts.data.orders,
      bulletins: parts.data.bulletins,
      workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
    };
  }, [people.data, service.data, parts.data, workOrders.data]);

  const cards = useMemo(() => (ctx ? customerCards(ctx) : []), [ctx]);
  const log = useMemo(() => (ctx ? notifications(ctx) : []), [ctx]);
  const summary = useMemo(() => customerSummary(cards, log), [cards, log]);

  const error = people.error ?? service.error ?? parts.error ?? workOrders.error;
  const loading = !ctx && !error;

  return (
    <SurfacePageLayout width="full">
      <Commentable name="CustomersScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-8" as="div">
            <SurfacePageHeader
              kicker="Customers"
              title="Customers"
              description={STANDFIRST}
              actions={
                <div className="flex items-center gap-2">
                  <span className="mono rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] text-muted dark:bg-zinc-800">
                    {summary.owners} customer accounts · {summary.contractors} contractors ·{' '}
                    {summary.notifications} notifications this fortnight
                  </span>
                  <HelpAnchor doc="customers/customers" />
                </div>
              }
            />

            <AskStrip view="customers" />

            {error ? (
              <InlineError>{`Customer data did not load: ${error.message}`}</InlineError>
            ) : loading ? (
              <SkeletonCardGrid count={6} />
            ) : cards.length === 0 ? (
              <EmptyState
                icon={<Building2 className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
                title="No customer organisations"
                description="The organisation register is empty."
              />
            ) : (
              <>
                <Commentable name="CustomerGrid">
                  <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2 @6xl:grid-cols-3">
                    {cards.map((card) => (
                      <CustomerOrgCard key={card.org} card={card} />
                    ))}
                  </div>
                </Commentable>

                <Commentable name="NotificationLog">
                  <div className="min-w-0">
                    <NotificationLog rows={log} />
                  </div>
                </Commentable>
              </>
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** Demo line 2745. One organisation's card. */
function CustomerOrgCard({ card }: { card: CustomerCard }) {
  const openRows = useRowsDrawer();
  const css = card.css;

  return (
    <article
      className="flex min-w-0 flex-col gap-3 rounded-2xl border p-4"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <header className="flex items-start gap-2.5">
        <span
          aria-hidden="true"
          className="mt-1 inline-block h-5 w-1.5 flex-shrink-0 rounded-full"
          style={{ background: card.record.colour }}
        />
        <div className="min-w-0">
          <h2
            className="truncate text-[15px] font-semibold tracking-tight"
            style={{ color: 'var(--ink-primary)' }}
          >
            {card.org}
          </h2>
          <p className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
            {card.record.unit}
          </p>
        </div>
        <span className="ml-auto flex-shrink-0">
          <TonePill tone={card.contractor ? 'warn' : 'flat'}>{card.relation}</TonePill>
        </span>
      </header>

      <dl className="grid grid-cols-1 gap-x-4 gap-y-0.5 text-[11.5px] @md:grid-cols-2">
        <Detail label="Registered" value={card.record.legal} />
        <Detail label="Address" value={card.record.addr} />
        <Detail label="Phone" value={card.record.phone} />
        <Detail label="Web" value={card.record.web} />
      </dl>

      <div
        className="grid grid-cols-2 gap-x-4 gap-y-1.5 border-y py-2.5 text-[11.5px] @md:grid-cols-3"
        style={{ borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }}
      >
        <Figure
          label="Fleet"
          value={groupNum(card.fleet.length)}
          note={card.contractorShare ? `${card.contractorShare} contractor-operated` : undefined}
        />
        <Figure
          label="Very poor or worse"
          value={String(card.veryPoorOrWorse)}
          colour={card.veryPoorOrWorse ? 'var(--band-vpoor)' : undefined}
        />
        <Figure label="Drafts to approve" value={String(card.drafts.length)} />
        <Figure label="Open cases" value={String(card.openCases.length)} />
        <Figure label="Open alerts" value={String(card.alerts.length)} />
        <Figure
          label="Satisfaction"
          value={css.overall != null ? css.overall.toFixed(1) : '—'}
          note={css.responses ? `${css.responses} responses` : 'no responses yet'}
        />
      </div>

      {css.responses ? (
        <p className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          Quality <b className="mono">{css.quality?.toFixed(1)}</b> · responsiveness{' '}
          <b className="mono">{css.responsiveness?.toFixed(1)}</b> · reporting{' '}
          <b className="mono">{css.reporting?.toFixed(1)}</b>
        </p>
      ) : null}

      <p className="text-[11.5px]" style={{ color: 'var(--ink-secondary)' }}>
        <span style={{ color: 'var(--ink-tertiary)' }}>Contacts: </span>
        {card.contacts.length
          ? card.contacts.map((p, i) => (
              <span key={p.name} title={p.email}>
                {i > 0 ? ', ' : ''}
                {p.name}{' '}
                <span style={{ color: 'var(--ink-tertiary)' }}>({p.role.split(' (')[0]})</span>
              </span>
            ))
          : '—'}
      </p>

      <p className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
        Channel: {card.channel} · Maintenance plan: {card.maintenancePlan}
      </p>

      <div>
        <div
          className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: 'var(--ink-tertiary)' }}
        >
          Latest notifications
        </div>
        <ul className="space-y-1.5">
          {card.notifications.map((n) => (
            <li key={n.key} className="flex gap-2 text-[11.5px]">
              <time
                className="mono flex-shrink-0 tabular-nums"
                style={{ color: 'var(--ink-tertiary)' }}
                dateTime={n.at}
              >
                {n.at.slice(5, 10)} {n.at.slice(11, 16)}
              </time>
              <span className="min-w-0" style={{ color: 'var(--ink-secondary)' }}>
                <b>{n.kind}</b> · {n.text}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1">
        <Link
          to={`/sites/${card.mine}`}
          className="text-[11.5px] font-medium underline decoration-dotted underline-offset-[3px] hover:decoration-solid"
          style={{ color: 'var(--accent)' }}
        >
          {card.siteName} site page
        </Link>
        <SourceLink
          onClick={() =>
            openRows('css', card.cssRows, {
              filter: `Site=${card.siteName}`,
              title: `CSS responses · ${card.siteName}`,
            })
          }
        >
          {card.cssRows.length} CSS responses
        </SourceLink>
        {card.openCases.length ? (
          <SourceLink
            onClick={() =>
              openRows('cases', card.openCases, {
                filter: `mine=${card.mine}`,
                title: `Open cases · ${card.org}`,
              })
            }
          >
            {card.openCases.length} open cases
          </SourceLink>
        ) : null}
        {card.drafts.length ? (
          <SourceLink
            onClick={() =>
              openRows('wos', card.drafts, {
                filter: `mine=${card.mine}, status=Draft…`,
                title: `Drafted work orders · ${card.org}`,
              })
            }
          >
            {card.drafts.length} drafts
          </SourceLink>
        ) : null}
        <button
          type="button"
          onClick={() => askOem(`Write the machine risk email for ${card.siteName}`)}
          className="rounded-lg border px-2.5 py-1 text-[11.5px] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          style={{ borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }}
        >
          Draft the weekly email
        </button>
      </div>
    </article>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 gap-1.5">
      <dt className="flex-shrink-0" style={{ color: 'var(--ink-tertiary)' }}>
        {label}
      </dt>
      <dd className="min-w-0" style={{ color: 'var(--ink-secondary)' }}>
        {value}
      </dd>
    </div>
  );
}

function Figure({
  label,
  value,
  note,
  colour,
}: {
  label: string;
  value: string;
  note?: string;
  colour?: string;
}) {
  return (
    <div className="min-w-0">
      <div
        className="text-[10px] font-semibold uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-tertiary)' }}
      >
        {label}
      </div>
      <div className="mono text-[15px] font-semibold leading-tight" style={{ color: colour }}>
        {value}
      </div>
      {note ? (
        <div className="text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          {note}
        </div>
      ) : null}
    </div>
  );
}

/** Demo line 2756. Every notification this fortnight, newest first. */
function NotificationLog({ rows }: { rows: readonly Notification[] }) {
  const shown = rows.slice(0, NOTIFICATION_LOG_LIMIT);
  return (
    <SceneSection
      title="Notification log"
      subtitle="all customers · drafted by the agent, sent by a person"
      actions={
        <span className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          {shown.length} of {rows.length}
        </span>
      }
    >
      <Table className="min-w-[880px]">
          <THead>
            <TR>
              <TH>When</TH>
              <TH>Customer</TH>
              <TH>Type</TH>
              <TH>Message</TH>
              <TH>To</TH>
              <TH>Channel</TH>
            </TR>
          </THead>
          <TBody>
            {shown.map((n) => (
              <TR key={n.key}>
                <TD className="mono whitespace-nowrap tabular-nums">
                  {n.at.slice(5, 10)} {n.at.slice(11, 16)}
                </TD>
                <TD className="whitespace-nowrap">{n.org === '*' ? 'All sites' : n.org}</TD>
                <TD>
                  <TonePill>{n.kind}</TonePill>
                </TD>
                <TD className="text-[11.5px]">{n.text}</TD>
                <TD className="text-[11.5px] whitespace-nowrap">{n.to || '—'}</TD>
                <TD className="text-[11px] whitespace-nowrap">{channelHead(n.org)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
    </SceneSection>
  );
}

/** Demo line 2756. The log prints only the first half of the channel string. */
const channelHead = (org: string): string => channelFor(org).split(' · ')[0];
